/**
 * useClinicMeetings — GET /v1/clinics/{clinic_id}/meetings
 *
 * Lists past and upcoming meetings for a clinic. New in backend v0.3.1.
 *
 * Powers two V1.1 surfaces:
 *   - Meeting selector dropdown on the dashboard page (next to clinic switcher)
 *   - Clinic-card morning-glance view on the clinic list page (upcoming + last 4)
 *
 * Sort: newest first by COALESCE(held_started_at, scheduled_at).
 *
 * Filters:
 *   - year: limit to a single performance year. Omit for all years.
 *   - includeCancelled: include status='cancelled' meetings. Default false.
 *   - limit: cap on result count. Default 50, max 200 per backend.
 *
 * Each item carries `is_pinnable_snapshot_available` — UI uses this to
 * grey out meetings that pre-date the first snapshot for the clinic
 * (clicking them would yield an empty dashboard).
 */
import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { MeetingListResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UseClinicMeetingsOptions {
  /** Filter to a specific performance year. Omit for all years. */
  year?: number;
  /** Include cancelled meetings. Default false. */
  includeCancelled?: boolean;
  /** Cap on result count. Default 50, max 200. */
  limit?: number;
  /** When `false`, the hook stays idle. Default: enabled iff clinicId is finite. */
  enabled?: boolean;
}

export function useClinicMeetings(clinicId: number, options: UseClinicMeetingsOptions = {}) {
  const { year, includeCancelled, limit, enabled } = options;
  return useQuery({
    queryKey: queryKeys.clinicMeetings.list(clinicId, {
      year,
      includeCancelled,
      limit,
    }),
    queryFn: async (): Promise<MeetingListResponse> => {
      const params: Record<string, string | number | boolean> = {};
      if (year !== undefined) params.year = year;
      if (includeCancelled !== undefined) params.include_cancelled = includeCancelled;
      if (limit !== undefined) params.limit = limit;
      const { data } = await getApiClient().get<MeetingListResponse>(
        `/v1/clinics/${clinicId}/meetings`,
        { params: Object.keys(params).length > 0 ? params : undefined },
      );
      return data;
    },
    staleTime: 30_000,
    enabled: enabled ?? Number.isFinite(clinicId),
  });
}
